import { useCampaignProjectLabels } from 'modules/campaigns/lib/hooks/useCampaignProjectLabels';
import { GoalEventType } from 'modules/goals/types';
import { DEFAULT_CAMPAIGN_FILTERS } from 'modules/missionControl/constants';
import { useMissionControlFilters } from 'modules/missionControl/hooks/useMissionControlFilters';
import { useQueryCampaignsForMetrics } from 'modules/missionControl/hooks/useQueryCampaignsForMetrics';
import { useQueryCampaignsHealthMetrics } from 'modules/missionControl/hooks/useQueryCampaignsHealthMetrics';
import { useQueryGoalCreators } from 'modules/missionControl/hooks/useQueryGoalCreators';
import { useQueryGoals } from 'modules/missionControl/hooks/useQueryGoals';
import { useQueryMessageTypes } from 'modules/missionControl/hooks/useQueryMessageTypes';
import { useQueryMissionControlUserPreferences } from 'modules/missionControl/hooks/useQueryMissionControlUserPreferences';
import { useQueryProjectCampaignAlerts } from 'modules/missionControl/hooks/useQueryProjectCampaignAlerts';
import { useQueryProjectGoals } from 'modules/missionControl/hooks/useQueryProjectGoals';
import { useQueryRecentCampaignAlerts } from 'modules/missionControl/hooks/useQueryRecentCampaignAlerts';
import { useTopCampaignsBySystemEventGoal } from 'modules/missionControl/hooks/useTopCampaignsBySystemEventGoal';
import { useTrackFilters } from 'modules/missionControl/hooks/useTrackFilters';
import {
  convertDateRangeToISOString,
  convertPresetToDateRange,
} from 'modules/missionControl/utils/conversions';
import { PropsWithChildren, useEffect, useMemo } from 'react';
import { DEFAULT_MISSION_CONTROL_CONTEXT, MissionControlContext } from './MissionControlContext';

export const MissionControlProvider: React.FC<PropsWithChildren> = ({ children }) => {
  // ========================================
  // Filter State Management
  // ========================================
  const {
    creatorIds,
    setCreatorIds,
    modifierIds,
    setModifierIds,
    clearFilters,
    dateRangePreset,
    setDateRangePreset,
    timezone,
    setTimezone,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    labelIds,
    setLabelIds,
    messageTypeIds,
    setMessageTypeIds,
    selectedFiltersCount,
  } = useMissionControlFilters();

  // Convert date range to string to pass to the backend
  const { startTime, endTime } = useMemo(
    () => convertDateRangeToISOString(startDate, endDate),
    [startDate, endDate],
  );

  // Save user filter preferences to the backend
  const { trackFilters } = useTrackFilters(labelIds, messageTypeIds, dateRangePreset);

  // ========================================
  // Data Queries
  // ========================================

  // Filters queries
  const messageTypesQuery = useQueryMessageTypes();
  const projectLabelsQuery = useCampaignProjectLabels();

  // Health metrics queries
  const campaignsHealthMetricsQuery = useQueryCampaignsHealthMetrics({
    labels: labelIds,
    messageTypes: messageTypeIds,
    startTime,
    endTime,
  });

  // Alerts-related queries
  const projectCampaignAlertsQuery = useQueryProjectCampaignAlerts();
  const recentCampaignAlertsQuery = useQueryRecentCampaignAlerts({
    labels: labelIds,
    messageTypes: messageTypeIds,
    startDate,
    endDate,
  });

  // User Preferences & Goals-related queries
  const projectGoalsQuery = useQueryProjectGoals({ includeArchived: false, pageSize: 10, page: 1 }); // we should refactor once we have more usages for this query
  const missionControlUserPreferencesQuery = useQueryMissionControlUserPreferences(undefined, {
    onSuccess: () => {
      projectGoalsQuery?.refetch(); // making sure to update the project goals query when the user preferences are updated
    },
  });
  const userGoalsQuery = useQueryGoals(
    missionControlUserPreferencesQuery?.userPreferences?.subscribedGoalIds || [],
  );
  const goalCreatorsQuery = useQueryGoalCreators({
    pageSize: 100,
    page: 1,
  }); // TODO: this is being used on to display the goal creators on the AddGoalSelect component, ideally the BE attach this data to the goal object.

  // Used to check if user has goals with system events to conditionally enable campaign queries
  const goalsWithSystemEvents = useMemo(
    () => userGoalsQuery.goals?.filter((goal) => goal.eventType === GoalEventType.System),
    [userGoalsQuery.goals],
  );

  // Campaign query parameters for system event goals metrics
  const campaignsForMetricsQueryParams = useMemo(
    () => ({
      startTime,
      endTime,
      campaignFiltersOpt: DEFAULT_CAMPAIGN_FILTERS,
    }),
    [startTime, endTime],
  );

  // Only enable campaigns query when we have system event goals and date range
  const campaignsForMetricsQueryOptions = useMemo(
    () => ({
      enabled: goalsWithSystemEvents?.length > 0 && !!startDate && !!endDate,
    }),
    [goalsWithSystemEvents, startDate, endDate],
  );

  // Get a list of campaigns that are eligible for metrics, it is used to get top campaigns for goals with system events.
  const campaignsForMetricsQuery = useQueryCampaignsForMetrics(
    campaignsForMetricsQueryParams,
    campaignsForMetricsQueryOptions,
  );

  // Returns top campaigns for each system event goal
  const { topCampaignsBySystemEventGoal } = useTopCampaignsBySystemEventGoal(
    startTime,
    endTime,
    campaignsForMetricsQuery?.campaigns,
    userGoalsQuery?.goals,
  );

  // ========================================
  // Side Effects
  // ========================================

  // Sync state with user preferences when they load
  useEffect(() => {
    const userPreferences = missionControlUserPreferencesQuery.userPreferences;
    if (userPreferences) {
      const { startDate: userStartDate, endDate: userEndDate } = convertPresetToDateRange(
        userPreferences.datePreset,
      );
      setDateRangePreset(userPreferences.datePreset);
      setStartDate(userStartDate);
      setEndDate(userEndDate);
      setLabelIds(userPreferences.campaignLabelIds);
      setMessageTypeIds(userPreferences.messageTypeIds);
    }
  }, [
    missionControlUserPreferencesQuery.userPreferences,
    setLabelIds,
    setMessageTypeIds,
    setDateRangePreset,
    setStartDate,
    setEndDate,
  ]);

  // ========================================
  // Context Provider
  // ========================================
  return (
    <MissionControlContext.Provider
      value={{
        // Filter state
        creatorIds,
        setCreatorIds,
        modifierIds,
        setModifierIds,
        dateRangePreset,
        setDateRangePreset,
        timezone,
        setTimezone,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        labelIds,
        setLabelIds,
        messageTypeIds,
        setMessageTypeIds,
        selectedFiltersCount,
        clearFilters,
        trackFilters,

        // Query data
        campaignsForMetricsQuery,
        campaignsHealthMetricsQuery,
        creatorsAndModifiersQuery: DEFAULT_MISSION_CONTROL_CONTEXT.creatorsAndModifiersQuery,
        goalCreatorsQuery,
        messageTypesQuery,
        missionControlUserPreferencesQuery,
        projectCampaignAlertsQuery,
        projectGoalsQuery,
        projectLabelsQuery,
        recentCampaignAlertsQuery,
        userGoalsQuery,
        topCampaignsBySystemEventGoal,
      }}
    >
      {children}
    </MissionControlContext.Provider>
  );
};
